/**
 * Constraints Editor
 * Allows Scheduler.Admin to view and edit term-level scheduling constraints.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { schedulerApi, SchedulerConstraints } from '../../services/scheduler-api';

const CARD = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: '12px',
  padding: '1.5rem',
} as const;

const SELECT_STYLE = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid var(--card-border)',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  fontSize: '14px',
  minWidth: '100px',
  cursor: 'pointer',
} as const;

interface FieldDef {
  key: keyof SchedulerConstraints;
  label: string;
  description: string;
  options: number[];
  default: number;
}

function range(start: number, end: number, step = 1): number[] {
  const result: number[] = [];
  for (let i = start; i <= end; i += step) result.push(i);
  return result;
}

const FIELDS: FieldDef[] = [
  {
    key: 'min_enroll',
    label: 'Min Enrollment',
    description: 'Minimum students required to open a section',
    options: range(2, 10),
    default: 2,
  },
  {
    key: 'target_enroll',
    label: 'Target Enrollment',
    description: 'Ideal class size for section planning',
    options: range(14, 30),
    default: 14,
  },
  {
    key: 'ft_max_sections',
    label: 'FT Max Sections',
    description: 'Max sections a full-time instructor can teach per term',
    options: range(4, 8),
    default: 4,
  },
  {
    key: 'adjunct_max_sections',
    label: 'Adjunct Max Sections',
    description: 'Max sections an adjunct instructor can teach per term',
    options: range(3, 6),
    default: 3,
  },
];

export function ConstraintsEditor({ onClose }: { onClose?: () => void } = {}) {
  const navigate = useNavigate();
  const [constraints, setConstraints] = useState<SchedulerConstraints | null>(null);
  const [draft, setDraft] = useState<SchedulerConstraints | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    schedulerApi
      .getConstraints()
      .then(c => {
        setConstraints(c);
        setDraft(c);
      })
      .catch(e => setError(e?.response?.data?.message || e?.message || 'Could not load constraints'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key: keyof SchedulerConstraints, value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      setDraft(prev => prev ? { ...prev, [key]: num } : prev);
    }
  };

  const handleReset = () => {
    if (constraints) setDraft({ ...constraints });
  };

  const handleRestoreDefaults = () => {
    setDraft({ min_enroll: 2, max_enroll: 30, target_enroll: 14, ft_max_sections: 4, adjunct_max_sections: 3 });
  };

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await schedulerApi.updateConstraints(draft);
      setConstraints(updated);
      setDraft(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleOptimizeTimeMatch = () => {
    setDraft(prev => prev ? {
      ...prev,
      min_enroll: 2,
      target_enroll: 14,
      ft_max_sections: 5,
      adjunct_max_sections: 4,
    } : prev);
  };

  const isDirty = draft && constraints
    ? JSON.stringify(draft) !== JSON.stringify(constraints)
    : false;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-primary)' }}>
        Loading constraints…
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--accent)', fontSize: '28px' }}>⚙️ Scheduling Constraints</h2>
          <p style={{ margin: '0.4rem 0 0 0', color: 'var(--text-secondary)' }}>
            Adjust term-level enrollment and instructor load limits
          </p>
        </div>
        {onClose ? (
          <button
            onClick={onClose}
            style={{ padding: '10px 20px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}
          >
            ✖ Close
          </button>
        ) : (
          <button
            onClick={() => navigate('/Scheduler')}
            style={{ padding: '10px 20px', background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}
          >
            ← Back to Scheduler
          </button>
        )}
      </div>

      <div style={CARD}>
        {error && (
          <p style={{ color: '#f87171', marginBottom: '1rem', fontSize: '13px' }}>❌ {error}</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {FIELDS.map(field => (
            <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap', paddingBottom: '1.25rem', borderBottom: '1px solid var(--card-border)' }}>
              <div style={{ flex: 1, minWidth: '220px' }}>
                <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '14px' }}>{field.label}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '3px' }}>{field.description}</div>
              </div>
              <select
                value={draft?.[field.key] ?? field.default}
                onChange={e => handleChange(field.key, e.target.value)}
                style={SELECT_STYLE}
              >
                {field.options.map(n => (
                  <option key={n} value={n}>{n}{n === field.default ? ' (default)' : ''}</option>
                ))}
              </select>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', minWidth: '80px' }}>
                range: {field.options[0]}–{field.options[field.options.length - 1]}
              </span>
            </div>
          ))}
          {/* Max Enrollment is fixed at 30 — not configurable */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap', paddingBottom: '1.25rem', borderBottom: '1px solid var(--card-border)' }}>
            <div style={{ flex: 1, minWidth: '220px' }}>
              <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '14px' }}>Max Enrollment</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '3px' }}>Hard cap on students per section</div>
            </div>
            <span style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', fontSize: '14px', minWidth: '100px', display: 'inline-block' }}>
              30 (fixed)
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            style={{
              padding: '10px 28px',
              background: 'var(--accent)',
              color: 'var(--button-text)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: isDirty ? 'pointer' : 'not-allowed',
              opacity: (saving || !isDirty) ? 0.6 : 1,
            }}
          >
            {saving ? '⏳ Saving…' : '💾 Save Changes'}
          </button>
          <button
            onClick={handleReset}
            disabled={!isDirty}
            style={{
              padding: '10px 20px',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--card-border)',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: isDirty ? 'pointer' : 'not-allowed',
              opacity: !isDirty ? 0.5 : 1,
            }}
          >
            Reset to Saved
          </button>
          <button
            onClick={handleRestoreDefaults}
            title="Restore to system defaults"
            style={{
              padding: '10px 20px',
              background: 'var(--bg-secondary)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--card-border)',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            ↺ Restore Defaults
          </button>
          <button
            onClick={handleOptimizeTimeMatch}
            title="Apply preset tuned for ≥80% time match"
            style={{
              padding: '10px 20px',
              background: 'rgba(99,102,241,0.15)',
              color: '#a5b4fc',
              border: '1px solid rgba(99,102,241,0.4)',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            🎯 Optimize for Time Match
          </button>
          {saved && (
            <span style={{ color: '#4ade80', fontSize: '13px' }}>✅ Saved successfully</span>
          )}
        </div>

        {draft && constraints && JSON.stringify({
          min_enroll: draft.min_enroll,
          target_enroll: draft.target_enroll,
          max_enroll: draft.max_enroll,
          ft_max_sections: draft.ft_max_sections,
          adjunct_max_sections: draft.adjunct_max_sections,
        }) === JSON.stringify({
          min_enroll: 2, target_enroll: 14, max_enroll: 30, ft_max_sections: 5, adjunct_max_sections: 4,
        }) && (
          <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '8px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.3)', fontSize: '13px', color: '#a5b4fc' }}>
            <strong>🎯 Time Match preset applied:</strong> Smaller sections (target 14) open more time slots;
            lower minimum (2) keeps more courses running; higher instructor caps provide the capacity.
            Click <strong>Save Changes</strong> to persist, then run the scheduler.
          </div>
        )}
      </div>

      {/* Note about SchedulingNotes overrides */}
      <div style={{ ...CARD, marginTop: '1rem', fontSize: '13px', color: 'var(--text-secondary)' }}>
        <strong style={{ color: 'var(--text-primary)' }}>Note:</strong> Individual instructor overrides
        (e.g. "Can teach 2 per quarter", "Do not schedule") are set via the{' '}
        <code style={{ color: 'var(--accent)' }}>SchedulingNotes</code> and{' '}
        <code style={{ color: 'var(--accent)' }}>DoNotSchedule</code> fields in the Azure{' '}
        <code style={{ color: 'var(--accent)' }}>Instructor</code> table in facman2.
        Contact Brian to update those values.
      </div>
    </div>
  );
}
